import React, { useState } from "react";
import {
  Checkbox,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@src/common/components";
import { FunnelIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FunnelIcon as FunnelIconSolid } from "@heroicons/react/24/solid";
import CourieButton from "@src/common/components/Button/Button";
import { useCandidates } from "../contexts/CandidatesProvider";
import { LabelsFilter } from "@src/common/components/LabelsFilter/LabelsFilter";
import { LabelEntityType } from "@api/graphql/generated/generated-types";

const LABEL_SELECT_OVERRIDES = {
  Popover: {
    props: {
      overrides: {
        Body: { style: { zIndex: 1000 } },
      },
    },
  },
};

export function DispatchCandidateFilterPopover() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    isDistanceFilterEnabled,
    setIsDistanceFilterEnabled,
    isVehicleTypeFilterEnabled,
    setIsVehicleTypeFilterEnabled,
    isWeightFilterEnabled,
    setIsWeightFilterEnabled,
    isShowDriverLabelsEnabled,
    setIsShowDriverLabelsEnabled,
    isShowDriverPayEnabled,
    setIsShowDriverPayEnabled,
    isShowDailyEarningsEnabled,
    setIsShowDailyEarningsEnabled,
    selectedDriverLabelIds,
    setSelectedDriverLabelIds,
  } = useCandidates();

  const hasActiveFilters =
    isDistanceFilterEnabled ||
    isVehicleTypeFilterEnabled ||
    isWeightFilterEnabled ||
    selectedDriverLabelIds.length > 0;

  // Only allow the popover to open via onOpenChange; close only via the
  // explicit button so clicks on the portalled Select dropdown don't
  // accidentally dismiss the popover.
  const handleOpenChange = (open: boolean) => {
    if (open) setIsOpen(true);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger>
        <CourieButton
          size="sm"
          color={hasActiveFilters ? "success" : "secondary"}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Filter dispatch candidates"
        >
          {hasActiveFilters ? (
            <FunnelIconSolid className="w-4 h-4" />
          ) : (
            <FunnelIcon className="w-4 h-4" />
          )}
        </CourieButton>
      </PopoverTrigger>
      <PopoverContent
        className="absolute z-30 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg shadow-lg p-4"
        style={{
          width: 260,
          minWidth: 260,
          maxWidth: 260,
          boxShadow:
            "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Filters
          </span>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Close filters"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="dispatch-filter-distance"
              checked={isDistanceFilterEnabled}
              onChange={(e) => setIsDistanceFilterEnabled(e.target.checked)}
              className="form-checkbox h-5 w-5"
            />
            <Label
              htmlFor="dispatch-filter-distance"
              className="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer"
            >
              Within 100 miles
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="dispatch-filter-vehicle"
              checked={isVehicleTypeFilterEnabled}
              onChange={(e) => setIsVehicleTypeFilterEnabled(e.target.checked)}
              className="form-checkbox h-5 w-5"
            />
            <Label
              htmlFor="dispatch-filter-vehicle"
              className="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer"
            >
              Matching vehicle type
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="dispatch-filter-weight"
              checked={isWeightFilterEnabled}
              onChange={(e) => setIsWeightFilterEnabled(e.target.checked)}
              className="form-checkbox h-5 w-5"
            />
            <Label
              htmlFor="dispatch-filter-weight"
              className="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer"
            >
              Enough weight capacity
            </Label>
          </div>
          <div className="border-t border-slate-200 dark:border-gray-700 pt-2 mt-1">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
              Driver Labels
            </div>
            <LabelsFilter
              entityType={LabelEntityType.Driver}
              selectedLabelIds={selectedDriverLabelIds}
              onLabelChange={setSelectedDriverLabelIds}
              placeholder="All drivers"
              size="mini"
              selectOverrides={LABEL_SELECT_OVERRIDES}
            />
          </div>
          <div className="border-t border-slate-200 dark:border-gray-700 pt-2 mt-1">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
              Display
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="dispatch-filter-show-driver-labels"
                checked={isShowDriverLabelsEnabled}
                onChange={(e) => setIsShowDriverLabelsEnabled(e.target.checked)}
                className="form-checkbox h-5 w-5"
              />
              <Label
                htmlFor="dispatch-filter-show-driver-labels"
                className="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer"
              >
                Show driver labels
              </Label>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Checkbox
                id="dispatch-filter-show-driver-pay"
                checked={isShowDriverPayEnabled}
                onChange={(e) => setIsShowDriverPayEnabled(e.target.checked)}
                className="form-checkbox h-5 w-5"
              />
              <Label
                htmlFor="dispatch-filter-show-driver-pay"
                className="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer"
              >
                Show driver pay (this week)
              </Label>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Checkbox
                id="dispatch-filter-show-daily-earnings"
                checked={isShowDailyEarningsEnabled}
                onChange={(e) =>
                  setIsShowDailyEarningsEnabled(e.target.checked)
                }
                className="form-checkbox h-5 w-5"
              />
              <Label
                htmlFor="dispatch-filter-show-daily-earnings"
                className="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer"
              >
                Show daily earnings
              </Label>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
